<template>
  <header>
    <a-form class="form" layout="vertical" ref="formRef" :model="formState">
      <a-form-item label="Tipo de ranking" name="type" required>
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="formState.type"
          placeholder="Selecciona el tipo"
        >
          <a-select-option :value="RankingType.Management">Por fecha de gestión</a-select-option>
          <a-select-option :value="RankingType.PayPromise">Por fecha de PDP</a-select-option>
        </a-select>
      </a-form-item>

      <a-form-item v-if="user?.customer?.type_business == 1" label="Empresa" name="entity" required>
        <entity-picker
          :showAll="false"
          defaultBehavior
          :value="formState.entity"
          @update:value="formState.entity = $event"
        />
      </a-form-item>

      <a-form-item label="Campaña" name="portfolio" required>
        <campaign-picker
          :showAll="false"
          :value="formState.portfolio"
          @update:value="formState.portfolio = $event"
          :entity="formState.entity"
        />
      </a-form-item>

      <a-form-item v-if="user?.customer?.enable_bases" label="Base" name="base" required>
        <bases-picker
          defaultBehavior
          :value="formState.base"
          @update:value="formState.base = $event"
          :entity="formState.entity"
          :portfolio="formState.portfolio"
        />
      </a-form-item>

      <a-form-item label="Fecha de inicio" name="startDate" required>
        <a-date-picker
          v-model:value="formState.startDate"
          format="DD/MM/YYYY"
          valueFormat="YYYY-MM-DD"
          placeholder="-- / -- / ----"
        />
      </a-form-item>

      <a-form-item label="Fecha de fin" name="endDate" required>
        <a-date-picker
          v-model:value="formState.endDate"
          format="DD/MM/YYYY"
          valueFormat="YYYY-MM-DD"
          placeholder="-- / -- / ----"
        />
      </a-form-item>
    </a-form>

    <div class="actions">
      <span v-if="totalRecords !== null" class="total">{{ totalRecords }} Registros</span>

      <a-tooltip placement="top">
        <template #title>
          <span v-if="isGlobalDownloading">Tiene una descarga pendiente</span>
          <span v-else>
            Se descargarán únicamente los datos de la tabla de acuerdo a los filtros seleccionados.
          </span>
        </template>

        <a-button
          type="link"
          :disabled="isButtonDisabled || isGlobalDownloading"
          :loading="isDownloading"
          @click="handleDownload"
        >
          Descargar
          <DownloadOutlined />
        </a-button>
      </a-tooltip>

      <a-button type="primary" :disabled="isButtonDisabled" @click="handleSearch">
        Buscar
        <SearchOutlined />
      </a-button>
    </div>
  </header>

  <a-table
    size="middle"
    id="ranking-pdp-table"
    :scroll="scrollStyle"
    :columns="tableColumns"
    :data-source="reportData?.results"
    :loading="isTableLoading"
    :pagination="false"
    bordered
  >
    <template #summary>
      <a-table-summary fixed>
        <a-table-summary-row>
          <a-table-summary-cell :index="0">Total</a-table-summary-cell>
          <template v-for="(column, index) in tableTotal" :key="column.date">
            <a-table-summary-cell :index="2 * index + 1">
              {{ column.total_pdp }}
            </a-table-summary-cell>
            <a-table-summary-cell :index="2 * index + 2">
              {{ column.total_amount }}
            </a-table-summary-cell>
          </template>
        </a-table-summary-row>
      </a-table-summary>
    </template>
  </a-table>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons-vue";
import type { TableColumnsType } from "ant-design-vue";

import type { Form, Report, PayPromise } from "@/@types/reports/payPromise";
import type { UserData } from "@/@types/global";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { getRankingColumns } from "@/app/reports/utils/tableColumns/payPromise/getRankingColumns";
import { fetchRanking, downloadRanking } from "@/app/reports/services";
import { getRankingTotal } from "@/app/reports/utils/helpers";

import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import BasesPicker from "@/app/shared/components/pickers/BasesPicker.vue";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";

enum RankingType {
  Management = "MANAGEMENT_DATE",
  PayPromise = "PROMISE_DATE",
}

const store = useStore();

const user = computed<UserData>(() => store.getters["getDataUser"]);
const { scrollStyle } = useScrollResize({ headerMargin: 460 });

// Form
const formState = reactive<Form>({
  type: null,
  portfolio: null,
  startDate: null,
  endDate: null,
});
const totalRecords = ref<number | null>(null);
const isButtonDisabled = computed(() => {
  for (const key in formState) {
    if (formState[key as keyof Form] === null) return true;
  }

  return false;
});
const isDownloading = ref(false);
const { isGlobalDownloading } = useGlobalDownloadState();

// Table
const tableColumns = ref<TableColumnsType | null>(null);
const reportData = ref<Report | null>(null);
const tableTotal = ref<PayPromise[]>([]);
const isTableLoading = ref(false);

const fetchReportData = async () => {
  isTableLoading.value = true;

  const response = await fetchRanking({ filters: formState });
  reportData.value = getRankingTotal(response);
  tableColumns.value = getRankingColumns(
    formState.startDate as string,
    formState.endDate as string
  );
  tableTotal.value = tableColumns.value
    .filter((column) => column.title !== "Usuario")
    .map((column) => {
      const dayTotal = reportData.value?.total.find((t) => t.date === column.key);

      return dayTotal
        ? {
            date: String(column.key),
            total_pdp: dayTotal.total_pdp,
            total_amount: dayTotal.total_amount,
          }
        : { date: String(column.key), total_pdp: 0, total_amount: 0 };
    });
  totalRecords.value = reportData.value.results.length;

  isTableLoading.value = false;
};

const handleSearch = async () => {
  await fetchReportData();
};

const handleDownload = async () => {
  isDownloading.value = true;
  await downloadRanking({ filters: formState });
  isDownloading.value = false;
};
</script>

<style scoped lang="sass">
header
  display: flex
  justify-content: space-between
  align-items: flex-end
  margin: 15px 0

.form
  display: flex
  gap: 10px

.ant-form-item
  margin: 0
  width: 180px

:deep(.ant-form-item-label)
  padding: 0

.actions
  display: flex
  align-items: center
  gap: 10px

:deep(#ranking-pdp-table .ant-table-cell.ant-table-cell-fix-right),
#ranking-pdp-table tfoot th,
#ranking-pdp-table tfoot td
  background-color: $gray-2 !important
</style>
